import { TitlePage } from "~/components";
import { Divider } from "antd";
import { Field, FormSpy } from "react-final-form";
import { composeValidators, required } from "~/utils/validate";
import { FormField } from "~/pages/questionnaires/shared";
import { NamesOfFieldEnum } from "~/typings/types";
import { configTitleFieldsOfSelfEmployed } from "../configTitleFields";
import { FullWidthRowFormContainer } from "~/pages/questionnaires/shared/form-field";
import { FloatingLabelSelect } from "~/components/floating-label-select";
import { V2Wizard } from "../../../../shared/wizard";
import styles from "./CommonSteps.module.scss";
import { useScrollAndFocusField, useSharedDataFromBackend } from "~/hooks";
import { useAnalyticsContext } from "~/services/analytics";
import { useCallback, useRef, useState } from "react";
import { postValidatePromocode } from "~/pages/questionnaires/v2/customer/api/apiMethods";
import type { RenderError } from "~/components/error-field";

interface AdditionalDetailsProps {
  queryPromocode?: string
}

export const AdditionalDetails = ({ queryPromocode }: AdditionalDetailsProps) => {
  useScrollAndFocusField(NamesOfFieldEnum.ClientFrom);

  const analytics = useAnalyticsContext();
  analytics?.trackStep('anketa-customer-se-init_step5');
  analytics?.trackStepWebvisor('webvisor_anketa_customer_step4');

  const { clientFromOrigins } = useSharedDataFromBackend();
  const clientFromOptions = Object.values(clientFromOrigins ?? {}).map(item => ({ label: item.title, value: item.name }));

  const [promocodeError, setPromocodeError] = useState<RenderError | null>(null);
  const [isPromocodeChecked, setIsPromocodeChecked] = useState(false);
  const lastCheckedValue = useRef<string | null>(null);

  const validatePromocode = useCallback(async (value: string) => {
    if (!value) {
      setPromocodeError(null);
      setIsPromocodeChecked(false);
      lastCheckedValue.current = null;
      return;
    }

    if (lastCheckedValue.current === value) {
      return;
    }
    try {
      const res = await postValidatePromocode({ promo_code: value });
      if (res?.data?.status === "not_found") {
        setPromocodeError({ has: 1, message: "Промокод не найден. Вы можете продолжить без него.", color: "#D97706" });
      } else {
        setPromocodeError({ has: 1, message: "Промокод успешно применен", color: "#437a54" });
      }
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error?.[0]?.message;
      const message = backendMessage ?? "Не удалось проверить промокод, попробуйте позже";
      setPromocodeError({ has: 1, message, color: "#D97706" });
    } finally {
      setIsPromocodeChecked(true);
      lastCheckedValue.current = value;
    }
  }, []);

  const handlePromocodeBlur = useCallback((eventOrValue: React.FocusEvent<HTMLInputElement> | string) => {
    const value = typeof eventOrValue === "string"
      ? eventOrValue.trim()
      : eventOrValue?.target?.value?.trim();

    validatePromocode(value ?? "");
  }, [validatePromocode]);

  return (
    <V2Wizard.Page>
      <TitlePage name="Укажите промокод партнёра" number="5" />
      <Divider style={{ margin: 0 }} />
      <div className={styles.container}>

        <FullWidthRowFormContainer>
          <FormField
            titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.ClientPromocode]}
            name={NamesOfFieldEnum.ClientPromocode}
            disabled={!!queryPromocode}
            descriptionField={isPromocodeChecked ? undefined : "Если вы подключались без партнёрского промокода, то нажмите «Продолжить»"}
            onBlurCallback={handlePromocodeBlur}
            renderError={promocodeError}
          />
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormField
            titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.ClientFrom]}
            name={NamesOfFieldEnum.ClientFrom}
          >
            <Field
              name={NamesOfFieldEnum.ClientFrom}
              subscription={{ submitFailed: true, error: true, submitError: true, value: true }}
              component={FloatingLabelSelect}
              titleField={configTitleFieldsOfSelfEmployed[NamesOfFieldEnum.ClientFrom]}
              optionsSelect={clientFromOptions}
            />
          </FormField>
        </FullWidthRowFormContainer>

        <FullWidthRowFormContainer>
          <FormSpy subscription={{ values: true }}>
            {propsSpy => (
              <>
                {propsSpy.values[NamesOfFieldEnum.ClientFrom] === "other" && (
                  <FormField
                    titleField={""}
                    name={NamesOfFieldEnum.ClientFromOther}
                    isColon={false}
                    required
                    validate={composeValidators(required())}
                  />
                )}
              </>
            )}
          </FormSpy>
        </FullWidthRowFormContainer>

      </div>
    </V2Wizard.Page>
  );
};
